import React, { useState, useMemo, useEffect } from "react";
import { HeaderPage } from "../base/headerPage";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { GridColumn as Column } from "@progress/kendo-react-grid";
import DataTable from "../../components/dataTable";
import { columnMenuProps } from "../../components/customColumnMenu";
import { getTenantCookieData, getUserCredential } from "../../lib/cookies";
import "./index.scss";
import { useDispatch } from "react-redux";
import { setNewMessage } from "../../redux/actions/setting-actions";
import { AddPayRequestDialog } from "src/pages/providerPayRequestDashboardNew/components/addPayRequest";
import ViewPayRequestDialog from "./components/ViewPayRequestDialog";
import { getPayRequestOverviewData, setPayRequestOverviewData } from "./utils/sessionStorage";
import { getDateRangeByPeriod, getMonthDateRange, getUserTimezone } from "./utils/dateUtils";
import { formatTimesheetQuantity } from "./utils/thresholdQuantity";
import { convertTimeZone } from "src/lib/date-time.util";
import { getProviderId } from "./services/getProviderId";
import { getProviderProfiles } from "./services/getProviderProfiles";
import { keysForScope, scopeParams, showProfileControl, storePositionFilter } from "./utils/selectedProfile";
import PositionFilter from "./components/PositionFilter";
import { getTimesheetsByProvider } from "./services/getTimesheetsByProvider";
import { deleteTimesheet } from "./services/deleteTimesheet";
import { applyDateFormat } from "../../utils/utils";
import { DEFAULT_SORT } from "../../constants/grid";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { useHistory } from "react-router-dom";
import { DialogComponent } from "../../components/dialog";
import { RowRender } from "../../components/renderers";
import { LoadingPanel } from "src/components/loadingPanel";
import { getProviderStatuses, getStatusByValue } from "../../components/StatusBadge/status";
import { GetSystemConfigDataByCode } from "../../lib/common-service";
import { convertHexToRgba } from "../../utils/utils";
import moment from "moment";

const loadingPanel = <LoadingPanel gridId="pay-request-overview-grid" />;

const getQueryParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  return {
    timesheetId: searchParams.get("id"), // timesheet_mstr_key from notification
    startDate: searchParams.get("startDate"),
    endDate: searchParams.get("endDate"),
    status: searchParams.get("status"), // status from notification
  };
};

// Field to parameter mapping: maps frontend field names to backend filter parameter names and table.column
// The 'column' property is used for both filtering AND sorting
const COLUMN_FILTER_MAPPING = {
  timesheet_mstr_key: { param: "in_status_filter", column: "t.timesheet_mstr_key" },
  status: { param: "in_status_filter", column: "tsm.display_name" },
  location_name: { param: "in_location_name_filter", column: "lm.name" },
  contracT_type_name: { param: "in_contract_type_name_filter", column: "tcm.name" },
  contract_name: { param: "in_contract_name_filter", column: "tm.name" },
  shift_type_name: { param: "in_shift_type_filter", column: "st.name" },
  ts_description: { param: "in_description_filter", column: "t.ts_description" },
  ts_date: { param: "in_service_date_filter", column: "t.ts_date" },
  post_date: { param: "in_post_date_filter", column: "t.post_date" },
  budget: { param: "in_budget_filter", column: "t.budget" },
  period_type: { param: "in_period_type_filter", column: "c.period_type" },
};

// Helper function to convert Date to SQL date-only format (YYYY-MM-DD)
const convertDateToSQLDateOnly = (dateValue) => {
  let date;

  if (dateValue instanceof Date) {
    date = dateValue;
  } else if (typeof dateValue === "string") {
    date = new Date(dateValue);
  } else {
    return "";
  }

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return "";
  }

  // Format: YYYY-MM-DD (date only, no time)
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

// Function to convert Kendo filter object to SQL filter string for a specific field
const convertKendoFilterToSQL = (filters, fieldName, tableColumn, fieldType = "string") => {
  if (!filters || filters.length === 0) return null;

  const filterParts = filters
    .map((filter) => {
      const { operator, value } = filter;

      // Handle date fields
      if (
        value &&
        (value instanceof Date || (typeof value === "string" && !isNaN(Date.parse(value)) && value.includes("-")))
      ) {
        const sqlDateValue = convertDateToSQLDateOnly(value);
        if (!sqlDateValue) return null;

        switch (operator) {
          case "eq":
            return `DATE(${tableColumn}) = DATE('${sqlDateValue}')`;
          case "neq":
            return `DATE(${tableColumn}) != DATE('${sqlDateValue}') OR ${tableColumn} IS NULL`;
          case "lt":
            return `DATE(${tableColumn}) < DATE('${sqlDateValue}')`;
          case "lte":
            return `DATE(${tableColumn}) <= DATE('${sqlDateValue}')`;
          case "gt":
            return `DATE(${tableColumn}) > DATE('${sqlDateValue}')`;
          case "gte":
            return `DATE(${tableColumn}) >= DATE('${sqlDateValue}')`;
          case "isnull":
            return `${tableColumn} IS NULL`;
          case "isnotnull":
            return `${tableColumn} IS NOT NULL`;
          case "isempty":
            return `(${tableColumn} IS NULL)`;
          case "isnotempty":
            return `(${tableColumn} IS NOT NULL)`;
          default:
            return `DATE(${tableColumn}) = DATE('${sqlDateValue}')`;
        }
      }

      // Handle number fields
      if (fieldType === "number") {
        // Handle null/undefined style operators first (they don't need a value)
        if (operator === "isnull" || operator === "isempty") {
          return `${tableColumn} IS NULL`;
        }
        if (operator === "isnotnull" || operator === "isnotempty") {
          return `${tableColumn} IS NOT NULL`;
        }

        // For other operators, parse the numeric value
        const numericValue = parseFloat(value);
        if (isNaN(numericValue)) return null;

        switch (operator) {
          case "eq":
            return `${tableColumn} = ${numericValue}`;
          case "neq":
            return `${tableColumn} != ${numericValue}`;
          case "lt":
            return `${tableColumn} < ${numericValue}`;
          case "lte":
            return `${tableColumn} <= ${numericValue}`;
          case "gt":
            return `${tableColumn} > ${numericValue}`;
          case "gte":
            return `${tableColumn} >= ${numericValue}`;
          default:
            return `${tableColumn} = ${numericValue}`;
        }
      }

      // Handle string fields
      const escapedValue = value ? String(value).replace(/'/g, "''") : "";

      switch (operator) {
        case "eq":
          return `${tableColumn} = '${escapedValue}'`;
        case "neq":
          return `${tableColumn} != '${escapedValue}'`;
        case "contains":
          return `${tableColumn} LIKE '%${escapedValue}%'`;
        case "doesnotcontain":
          return `${tableColumn} NOT LIKE '%${escapedValue}%'`;
        case "startswith":
          return `${tableColumn} LIKE '${escapedValue}%'`;
        case "endswith":
          return `${tableColumn} LIKE '%${escapedValue}'`;
        case "isempty":
          // Treat empty as either NULL or empty string
          return `(${tableColumn} IS NULL OR ${tableColumn} = '')`;
        case "isnotempty":
          // Treat not empty as both NOT NULL and not empty string
          return `(${tableColumn} IS NOT NULL AND ${tableColumn} <> '')`;
        case "isnull":
          return `${tableColumn} IS NULL`;
        case "isnotnull":
          return `${tableColumn} IS NOT NULL`;
        default:
          return `${tableColumn} = '${escapedValue}'`;
      }
    })
    .filter((part) => part !== null);

  if (filterParts.length === 0) return null;

  // Join multiple filters with AND
  const sqlFilter = filterParts.length > 1 ? `(${filterParts.join(" AND ")})` : filterParts[0];

  return ` AND ${sqlFilter} `;
};

// Function to extract column filters from filterObj and return dedicated filter parameters
const extractColumnFilters = (filterObj, columns = []) => {
  const columnFilters = {
    in_status_filter: null,
    in_location_name_filter: null,
    in_contract_type_name_filter: null,
    in_contract_name_filter: null,
    in_shift_type_filter: null,
    in_description_filter: null,
    in_service_date_filter: null,
    in_post_date_filter: null,
    in_budget_filter: null,
    in_period_type_filter: null,
  };

  if (!filterObj || !filterObj.filters || filterObj.filters.length === 0) {
    return columnFilters;
  }

  // Helper function to get field type from columns array
  const getFieldType = (fieldName) => {
    const column = columns.find((col) => col.field === fieldName);
    return column ? column.type || "string" : "string";
  };

  // Step 1: Group all filters by field name
  // This ensures multiple filters on the same column are aggregated together
  const filtersByField = {};

  filterObj.filters.forEach((filterGroup) => {
    if (filterGroup.filters && Array.isArray(filterGroup.filters)) {
      // Nested filter structure
      filterGroup.filters.forEach((nestedFilter) => {
        if (nestedFilter.field) {
          if (!filtersByField[nestedFilter.field]) {
            filtersByField[nestedFilter.field] = [];
          }
          // Collect all filter conditions for this field
          const conditions = nestedFilter.filters || [nestedFilter];
          filtersByField[nestedFilter.field].push(...conditions);
        }
      });
    } else if (filterGroup.field) {
      // Direct filter
      if (!filtersByField[filterGroup.field]) {
        filtersByField[filterGroup.field] = [];
      }
      const conditions = filterGroup.filters || [filterGroup];
      filtersByField[filterGroup.field].push(...conditions);
    }
  });

  // Step 2: Process each field once with ALL its filters
  // This prevents filters from being overwritten
  Object.keys(filtersByField).forEach((fieldName) => {
    if (COLUMN_FILTER_MAPPING[fieldName]) {
      const mapping = COLUMN_FILTER_MAPPING[fieldName];
      const allFiltersForField = filtersByField[fieldName];
      const fieldType = getFieldType(fieldName);

      // convertKendoFilterToSQL already handles multiple conditions with AND
      const sqlFilter = convertKendoFilterToSQL(allFiltersForField, fieldName, mapping.column, fieldType);

      if (sqlFilter) {
        columnFilters[mapping.param] = sqlFilter;
      }
    }
  });

  return columnFilters;
};

// Custom Status Cell Renderer Component
const StatusCell = (props) => {
  const status = props.dataItem.status;
  const statusData = status.display_name;
  const statusColor = status.color_code;
  const statusColorRgba = convertHexToRgba(statusColor, 0.1);
  const statusColorStyle = {
    backgroundColor: statusColorRgba,
    borderColor: statusColor,
    color: statusColor,
  };

  return (
    <td className="k-table-td" style={{ textAlign: "center", verticalAlign: "middle" }}>
      <span style={statusColorStyle} className="status-badge">
        {statusData}
      </span>
    </td>
  );
};

// Tab 0 is All; later tabs follow getProviderStatuses() order, not mstr_key.
// Key 8 (attestation_required) is tab 2 — treating key as index landed on Rejected.
const getTabIndexFromStatusKey = (statusMstrKey) => {
  if (!statusMstrKey) return 0;
  const idx = getProviderStatuses().findIndex((s) => s.timesheet_status_mstr_key === Number(statusMstrKey));
  return idx === -1 ? 0 : idx + 1;
};

const getStatusKeyFromTab = (tabIndex, statusList) => {
  if (tabIndex === 0) return null; // All
  return statusList[tabIndex - 1]?.timesheet_status_mstr_key || null;
};

const PayRequestOverview = (pageProps) => {
  const cookiesData = getUserCredential();
  const dispatch = useDispatch();
  const history = useHistory();
  // Router-supplied permission for the `pay_request_overview` menu â€” the web
  // counterpart of the mobile app's add_pay_request writeAccess check.
  const hasWriteAccess = pageProps.writeAccess === 1;

  // Determine initial tab from location.state or session storage
  const getInitialTab = () => {
    const queryParams = getQueryParams();
    if (queryParams.timesheetId) {
      return 0; // Notification access, so All
    }

    // Check for status master key in query params
    if (queryParams.status) {
      const statusKey = parseInt(queryParams.status, 10);
      if (!isNaN(statusKey)) {
        return getTabIndexFromStatusKey(statusKey);
      }
    }

    // First check location.state for timesheet_status_mstr_key
    const statusKeyFromState = pageProps.location?.state?.timesheet_status_mstr_key;
    if (statusKeyFromState) {
      return getTabIndexFromStatusKey(statusKeyFromState);
    }

    // Fallback to session storage
    const storedData = getPayRequestOverviewData();
    if (storedData?.timesheet_status_mstr_key) {
      return getTabIndexFromStatusKey(storedData.timesheet_status_mstr_key);
    }

    return 0; // Default to All tab
  };

  const [selectedTab, setSelectedTab] = useState(getInitialTab);
  const [isAPICalling, setAPICallingState] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [isRefreshLoading, setRefreshLoading] = useState(false);
  const [attestationEnabled, setAttestationEnabled] = useState(undefined);
  const [holdEnabled, setHoldEnabled] = useState(undefined);
  const statusList = getProviderStatuses({ attestationEnabled, holdEnabled });

  // Calculate initial dates based on periodFilter from navigation
  const getInitialDates = () => {
    // Notification access, so PYTD
    const queryParams = getQueryParams();
    const hasLocationState = pageProps.location?.state?.periodFilter;
    const hasSessionData = getPayRequestOverviewData()?.periodFilter;

    // Check location.state first (from navigation)
    if (pageProps.location?.state?.startDate && pageProps.location?.state?.endDate) {
      return {
        startDate: moment(pageProps.location.state.startDate, "YYYY-MM-DD").toDate(),
        endDate: moment(pageProps.location.state.endDate, "YYYY-MM-DD").toDate(),
      };
    }

    if (queryParams.startDate && queryParams.endDate) {
      return {
        startDate: moment(queryParams.startDate, "YYYY-MM-DD").toDate(),
        endDate: moment(queryParams.endDate, "YYYY-MM-DD").toDate(),
      };
    }

    if (queryParams.timesheetId || (!hasLocationState && !hasSessionData)) {
      return getDateRangeByPeriod("PYTD");
    }

    // First check location.state
    let periodFilter = pageProps.location?.state?.periodFilter;
    let currentMonthStr = pageProps.location?.state?.currentMonth;

    // Fallback to session storage
    if (!periodFilter) {
      const storedData = getPayRequestOverviewData();
      if (storedData) {
        periodFilter = storedData.periodFilter;
        currentMonthStr = storedData.currentMonth;
      }
    }

    // If we have a periodFilter, calculate dates accordingly
    if (periodFilter) {
      if (periodFilter === "Monthly" && currentMonthStr) {
        const currentMonth = new Date(currentMonthStr);
        return getMonthDateRange(currentMonth);
      }
      return getDateRangeByPeriod(periodFilter);
    }

    // Default: PYTD
    return getDateRangeByPeriod("PYTD");
  };

  const initialDates = getInitialDates();
  const [startDate, setStartDate] = useState(initialDates.startDate);
  const [endDate, setEndDate] = useState(initialDates.endDate);
  const [dateErrors, setDateErrors] = useState({ startDate: "", endDate: "" });

  // Edit dialog state â€” holds the row being edited, or null when closed. The
  // row doubles as the `task` prop that puts AddPayRequestDialog into edit mode.
  const [editTask, setEditTask] = useState(null);
  const [viewDialogVisible, setViewDialogVisible] = useState(false);
  const [isRowSelected, setIsRowSelected] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);

  // Delete confirmation dialog state
  const [confirmationPopup, setConfirmationPopup] = useState({
    title: "Delete",
    desc: "Are you sure you want to delete this timesheet?",
    isVisible: false,
    type: "",
    actionBtn: [],
    data: {},
    action: "Delete",
  });

  // Pagination state management
  const defaultPageSize = getTenantCookieData("record_per_page");
  const [pageData, setPageData] = useState({
    skip: 0,
    take: defaultPageSize.recordTake,
  });
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [totalRows, setTotalRows] = useState(0);

  // Filter and sort state management
  const [filterData, setFilterData] = useState({
    isSort: false,
    isFilter: false,
    data: "",
    in_sort_column: "t.timesheet_mstr_key",
    in_sort_number: DEFAULT_SORT.number,
    sort_order: DEFAULT_SORT.order,
  });
  const [filterField, setFilterField] = useState({
    logic: "and",
    filters: [],
  });
  const [sortField, setSortField] = useState([]);
  const [gridColumns, setGridColumns] = useState([]);

  // Manage columns state management
  const [columnsData, setColumnsData] = useState([]);
  const [isCheckColumn, setColumnCheck] = useState(false);

  // Reset grid state for refresh functionality
  const [resetGrid, setResetGrid] = useState(false);

  // Export state management
  const [exportData, setExportData] = useState([]);
  const [isExportDataClick, setIsExportDataClick] = useState(false);
  const fileName = "Pay_Request_Overview";

  const [payRequestData, setPayRequestData] = useState([]);

  // Direct access state (for notification navigation)
  const [isDirectAccess, setIsDirectAccess] = useState(false);
  const [directAccessProviderId, setDirectAccessProviderId] = useState(null);
  const [isLoadingProvider, setIsLoadingProvider] = useState(false);
  const [autoOpenTimesheetId, setAutoOpenTimesheetId] = useState(null);
  // every profile of the logged-in user, for the Position filter and direct access
  const [profileRows, setProfileRows] = useState([]);
  // null until the user changes the Position filter; until then the lists the page was opened with apply
  const [pickedPositionKeys, setPickedPositionKeys] = useState(null);

  useEffect(() => {
    const loadHoldPref = async () => {
      const prefs = await GetSystemConfigDataByCode(
        {
          in_tenant_id: cookiesData.out_tenant_id,
          pref_code: ["sys_hold_status_notif"],
        },
        history,
        dispatch
      );
      setHoldEnabled(prefs?.sys_hold_status_notif === "1");
    };
    loadHoldPref();
  }, [cookiesData.out_tenant_id, dispatch, history]);

  // getInitialTab runs before the pref resolves, so selectedTab indexes the unfiltered
  // list. Once hold is off, statusList loses a row and every later tab shifts down.
  useEffect(() => {
    if (holdEnabled !== false) return;
    const holdTab = getProviderStatuses().findIndex((s) => s.value === "hold") + 1;
    setSelectedTab((tab) => (tab === holdTab ? 0 : tab > holdTab ? tab - 1 : tab));
  }, [holdEnabled]);

  const isBusy = isRefreshLoading || isAPICalling || !initialLoading;

  // Position lists for get-by-provider-by-status. Direct access (no navigation state and no
  // stored dashboard data) shows All Positions. Otherwise the lists travel with provider_id and are
  // used only for that provider: from the navigation state when it carries a provider_id, else from
  // the stored dashboard data. A pick in the Position filter overrides all of them.
  const getPositionScope = (providerId) => {
    if (pickedPositionKeys !== null) {
      return scopeParams(profileRows, pickedPositionKeys);
    }
    if (isDirectAccess) {
      return scopeParams(profileRows, []);
    }
    const navigationState = pageProps.location?.state;
    const source = navigationState?.provider_id ? navigationState : getPayRequestOverviewData();
    if (!source || String(source.provider_id) !== String(providerId)) {
      return {};
    }
    return {
      ...(source.provider_ids ? { in_provider_ids: source.provider_ids } : {}),
      ...(source.org_position_keys ? { in_org_position_keys: source.org_position_keys } : {}),
    };
  };

  const handleTabSelect = (tabIndex) => {
    setSelectedTab(tabIndex);
  };

  const currentProviderId =
    pageProps.location?.state?.provider_id ||
    getPayRequestOverviewData()?.provider_id ||
    directAccessProviderId ||
    null;

  // The filter lists the logged-in user's own positions, so it shows only on their own pay requests.
  // Until a pick it shows the lists the grid was loaded with.
  const showPositionFilter =
    showProfileControl(profileRows) &&
    profileRows.some((row) => String(row.provider_mstr_key) === String(currentProviderId));
  const positionFilterKeys = showPositionFilter
    ? (pickedPositionKeys ?? keysForScope(profileRows, getPositionScope(currentProviderId), currentProviderId))
    : [];

  const handlePositionFilterChange = (keys) => {
    const nextScope = scopeParams(profileRows, keys);
    const lists = {
      provider_ids: nextScope.in_provider_ids ?? null,
      org_position_keys: nextScope.in_org_position_keys ?? null,
    };
    setPickedPositionKeys(keys);
    storePositionFilter(keys);
    // A reload reads the lists from where the page was opened with them, so they are updated there
    if (pageProps.location?.state?.provider_id) {
      history.replace({ ...pageProps.location, state: { ...pageProps.location.state, ...lists } });
    } else if (getPayRequestOverviewData()?.provider_id) {
      setPayRequestOverviewData({ ...getPayRequestOverviewData(), ...lists });
    }
  };

  const handleStartDateChange = (event) => {
    const newStartDate = event.target.value;

    // Validate that start date is not after end date
    if (newStartDate && endDate && newStartDate > endDate) {
      setDateErrors((prev) => ({ ...prev, startDate: "Start date must be before end date" }));
      return;
    }

    setDateErrors((prev) => ({ ...prev, startDate: "" }));
    setStartDate(newStartDate);
  };

  const handleEndDateChange = (event) => {
    const newEndDate = event.target.value;

    // Validate that end date is not before start date
    if (startDate && newEndDate && startDate > newEndDate) {
      setDateErrors((prev) => ({ ...prev, endDate: "End date must be after start date" }));
      return;
    }

    setDateErrors((prev) => ({ ...prev, endDate: "" }));
    setEndDate(newEndDate);
  };

  const handleFetchData = async () => {
    // When searching again, clear all grid filters and sorting so the new
    // search starts from a clean state and UI indicators are reset.
    setFilterField({ logic: "and", filters: [] });
    setSortField([]);
    setFilterData({
      isSort: false,
      isFilter: false,
      data: "",
      in_sort_column: "t.timesheet_mstr_key",
      in_sort_number: DEFAULT_SORT.number,
      sort_order: DEFAULT_SORT.order,
    });

    // Reset to first page using current page size
    setPageData({ skip: 0, take: pageSize.recordTake });

    // Ask DataTable/Kendo Grid to reset its internal sort/filter state & UI
    setResetGrid(true);

    // Refetch data with explicit default sort and no column filters
    await fetchTimesheetsByProvider(
      0,
      pageSize.recordTake,
      "t.timesheet_mstr_key",
      DEFAULT_SORT.number,
      DEFAULT_SORT.order,
      null
    );
  };

  // const handleSearchChange = (event) => {
  //     setSearchValue(event.target.value);
  // };

  // Helper functions for filter and pagination
  const getInFilter = () => {
    return filterData.isFilter ? filterData.data : null;
  };

  const getInSortColumn = () => {
    return filterData.isSort ? filterData.in_sort_column : "t.timesheet_mstr_key";
  };

  const getInSortNumber = () => {
    return filterData.isSort ? filterData.in_sort_number : DEFAULT_SORT.number;
  };

  const getInSortOrder = () => {
    return filterData.isSort ? filterData.sort_order : DEFAULT_SORT.order;
  };

  const getPageSkip = () => {
    return pageData.skip;
  };

  const getPageTake = () => {
    return pageData.take;
  };

  // Pagination handler
  const handlePageChange = async (event) => {
    // Reuse current visible columns for extracting column filters
    const columnsToUse = gridColumns.length > 0 ? gridColumns : columns;
    const columnFilters = extractColumnFilters(filterField, columnsToUse);

    // Preserve current sort when changing page / page size
    const sortColumn = getInSortColumn();
    const sortNumber = getInSortNumber();
    const sortOrder = getInSortOrder();

    // Check if this is a per-page dropdown change
    if (event.syntheticEvent?.target?.value?.recordTake !== undefined) {
      // Per-page dropdown was changed
      const newPageSize = event.syntheticEvent.target.value;
      const take = newPageSize.recordTake;

      setPageSize(newPageSize);
      setPageData({
        skip: 0,
        take,
      });

      // Fetch first page with new page size, keeping filters & sorting
      await fetchTimesheetsByProvider(0, take, sortColumn, sortNumber, sortOrder, columnFilters);
    } else {
      // Regular pagination navigation
      const newPageData = event.page;
      setPageData({
        skip: newPageData.skip,
        take: newPageData.take,
      });

      // Fetch data for the new page, keeping filters & sorting
      await fetchTimesheetsByProvider(
        newPageData.skip,
        newPageData.take,
        sortColumn,
        sortNumber,
        sortOrder,
        columnFilters
      );
    }
  };

  // Column menu handler for filtering and sorting
  const handleColumnMenu = async (columnMenuData, isFilter, isSort, filterObj) => {
    let filterSkip = getPageSkip();
    let filterTake = getPageTake();

    if (isSort) {
      setSortField(columnMenuData);
    } else {
      filterSkip = 0;
      filterTake = pageSize.recordTake;
      setFilterField(filterObj);
      setPageData({ skip: filterSkip, take: filterTake });
    }

    // Extract column filters from filterObj and convert to dedicated parameters
    // When sorting is applied without filterObj, use existing filterField state to preserve filters
    const filterObjToUse = filterObj || (isSort ? filterField : null);
    const columnFilters = extractColumnFilters(filterObjToUse, columns);

    isFilter = Object.values(columnFilters).some((filter) => filter !== null);

    let sortDir = filterData.isSort && columnMenuData.length ? filterData.sort_order : DEFAULT_SORT.order;
    let sortColumn = filterData.isSort && columnMenuData.length ? filterData.in_sort_column : "t.timesheet_mstr_key"; // Default sort by timesheet key
    let sortNumber = filterData.isSort && columnMenuData.length ? filterData.in_sort_number : DEFAULT_SORT.number;

    isSort = !columnMenuData.length ? false : filterData.isSort;

    if (columnMenuData[0] !== undefined && columnMenuData[0].dir !== undefined) {
      isSort = true;
      sortDir = columnMenuData[0].dir.toUpperCase();
      // Get the database column name from the mapping
      const fieldName = columnMenuData[0].field;
      if (COLUMN_FILTER_MAPPING[fieldName]) {
        sortColumn = COLUMN_FILTER_MAPPING[fieldName].column;
      } else {
        // Default mapping for fields not in COLUMN_FILTER_MAPPING
        sortColumn = "t.timesheet_mstr_key";
      }
      // Calculate sort number (column index) for backward compatibility
      let index = 1;
      if (payRequestData.length > 0 && columnMenuData[0] !== undefined) {
        let obj = Object.keys(payRequestData[0]);
        index = obj.findIndex((x) => x === columnMenuData[0].field) + 1;
      }
      sortNumber = index;
    }

    setFilterData({
      ...filterData,
      isFilter: isFilter,
      isSort: isSort,
      data: "", // We're using dedicated parameters now
      in_sort_column: sortColumn,
      in_sort_number: sortNumber,
      sort_order: sortDir,
    });

    // Fetch data with filters
    await fetchTimesheetsByProvider(filterSkip, filterTake, sortColumn, sortNumber, sortDir, columnFilters);
  };

  // Clear filter handler
  const onClearFilter = () => {
    setFilterData({
      isSort: false,
      isFilter: false,
      data: "",
      in_sort_column: "t.timesheet_mstr_key",
      in_sort_number: DEFAULT_SORT.number,
      sort_order: DEFAULT_SORT.order,
    });
    setFilterField({
      logic: "and",
      filters: [],
    });

    // Reset to first page
    setPageData({ skip: 0, take: pageData.take });

    // Refetch data without filters
    fetchTimesheetsByProvider(0, pageData.take, null, DEFAULT_SORT.number, DEFAULT_SORT.order);
  };

  // Refresh handler - keeps filters and sorting, resets pagination, and refetches data
  const onRefreshClick = async () => {
    setPageData({ skip: 0, take: pageSize.recordTake });
    setIsRowSelected(false);
    setSelectedRowData(null);
    // Do NOT reset resetGrid here so Kendo Grid retains its current filters/sorting UI
    // setResetGrid(true);

    // Refetch data keeping current filters & sort
    const columnsToUse = gridColumns.length > 0 ? gridColumns : columns;
    const columnFilters = extractColumnFilters(filterField, columnsToUse);
    const sortColumn = getInSortColumn();
    const sortNumber = getInSortNumber();
    const sortOrder = getInSortOrder();

    await fetchTimesheetsByProvider(0, pageSize.recordTake, sortColumn, sortNumber, sortOrder, columnFilters);
  };

  // Row click handler for selection (supports both direct event and RowRender props signature)
  const handleRowClick = (propsOrEvent, e) => {
    // Support both RowRender signature (props, e) and direct event signature (event)
    const dataItem = propsOrEvent?.originalProps?.dataItem || propsOrEvent?.dataItem;
    const clickedRowKey = dataItem?.timesheet_mstr_key;
    const selectedRowKey = selectedRowData?.timesheet_mstr_key;

    // If clicking the same row that's already selected, toggle off (deselect)
    if (clickedRowKey === selectedRowKey && isRowSelected) {
      setSelectedRowData(null);
      setIsRowSelected(false);
      // Clear selection on all rows
      const newData = payRequestData.map((item) => ({ ...item, selected: false }));
      setPayRequestData(newData);
    } else {
      // Select the clicked row
      setSelectedRowData(dataItem);
      setIsRowSelected(true);
      // Update data with selected property
      const newData = payRequestData.map((item) => ({
        ...item,
        selected: item.timesheet_mstr_key === clickedRowKey,
      }));
      setPayRequestData(newData);
    }
  };

  /*
   * Custom row renderer to ensure clicks anywhere on the row trigger selection.
   *
   * onRowDoubleClick has to be handed to RowRender as well, not just to the
   * Grid: RowRender clones the row and overwrites the tr's onDoubleClick with
   * its own, so Kendo's handler never runs while this renderer is in place.
   * Passing it here is also the better path â€” Kendo only fires row double-click
   * when the event target is the TD itself, which misses every cell whose
   * content sits inside a span.
   */
  const customRowRender = (tr, props) => (
    <RowRender originalProps={props} tr={tr} onRowClick={handleRowClick} onRowDoubleClick={handleRowDoubleClick} />
  );

  // Handle column reorder from drag-and-drop in the grid
  const handleColumnReorder = (reorderedColumns) => {
    // Map Kendo column objects back to our column config format
    const newColumnOrder = reorderedColumns
      .filter((col) => col.field) // Filter out any columns without field
      .map((kendoCol) => {
        // Find the matching column from gridColumns or columns
        const sourceColumns = gridColumns.length > 0 ? gridColumns : columns;
        return sourceColumns.find((c) => c.field === kendoCol.field);
      })
      .filter(Boolean); // Remove any undefined entries

    setGridColumns(newColumnOrder);
    setColumnsData(newColumnOrder);
  };

  const selectedRowIsNewStatus = (row = selectedRowData) =>
    row?.status?.value === getStatusByValue("new").value ||
    row?.status?.value === getStatusByValue("rejected").value;

  /*
   * Double-click a row to open its read-only detail view. This works for every
   * status, so there is no status gate here â€” editing is the status-restricted
   * action and stays on the Action column's pencil.
   */
  const handleRowDoubleClick = (event) => {
    // Two callers with different shapes: customRowRender/RowRender passes its
    // own props, and Kendo's Grid passes an event carrying `dataItem`.
    const dataItem = event?.dataItem || event?.originalProps?.dataItem;
    if (!dataItem) return;
    setSelectedRowData(dataItem);
    setViewDialogVisible(true);
  };

  // Row-level Action column handlers.
  const handleEditRow = (row) => {
    setEditTask(row);
  };

  const handleDeleteRow = (row) => {
    setConfirmationPopup({
      ...confirmationPopup,
      title: "Delete",
      desc: "Are you sure you want to delete this record?",
      isVisible: true,
      type: "DELETE_CONFIRM",
      actionBtn: [
        { name: "CANCEL", onClick: onCancelConfirm },
        { name: "YES", onClick: () => onDeleteConfirm(row) },
      ],
      data: row,
      action: "Delete",
    });
  };

  // Cancel delete confirmation
  const onCancelConfirm = () => {
    setConfirmationPopup({
      ...confirmationPopup,
      isVisible: false,
      type: "",
      actionBtn: [],
      data: {},
      action: "",
    });
  };

  // Re-run the current query, preserving filters, sorting and the page the user
  // is on. Both delete and edit go through here rather than patching the row
  // locally: the server recalculates budgets across the contract's period, so a
  // sibling row's amount can change as a side effect of the one we touched.
  const refetchGrid = async () => {
    setRefreshLoading(true);

    const columnsToUse = gridColumns.length > 0 ? gridColumns : columns;
    const columnFilters = extractColumnFilters(filterField, columnsToUse);
    const sortColumn = getInSortColumn();
    const sortNumber = getInSortNumber();
    const sortOrder = getInSortOrder();

    await fetchTimesheetsByProvider(pageData.skip, pageData.take, sortColumn, sortNumber, sortOrder, columnFilters);
  };

  // Confirm delete action. The row is passed in rather than read from state:
  // this callback is stored in `confirmationPopup.actionBtn` at the moment the
  // popup opens, so it would close over the pre-click value of any state set in
  // the same handler.
  const onDeleteConfirm = async (row) => {
    setConfirmationPopup({
      ...confirmationPopup,
      isVisible: false,
      type: "",
      actionBtn: [],
      data: {},
      action: "Delete",
    });

    if (!row?.timesheet_mstr_key) return;

    setRefreshLoading(true);
    // deleteTimesheet raises its own success/error toast â€” do not add another.
    const result = await deleteTimesheet(cookiesData, dispatch, row.timesheet_mstr_key);

    if (result && result.status) {
      // The deleted row may have been the selected one.
      if (selectedRowData?.timesheet_mstr_key === row.timesheet_mstr_key) {
        setSelectedRowData(null);
        setIsRowSelected(false);
      }
      await refetchGrid();
    } else {
      setRefreshLoading(false);
    }
  };

  const fetchTimesheetsByProvider = async (
    skip = null,
    take = null,
    sortColumn = null,
    sortNumber = null,
    sortOrder = null,
    columnFilters = null
  ) => {
    if (!cookiesData?.secretToken) return;

    const queryParams = getQueryParams();
    const isNotificationAccess = !!queryParams.timesheetId || !!queryParams.status || isDirectAccess;

    // First check location.state (for backward compatibility)
    let taskCategoryMstrKey = pageProps.location.state?.task_category_mstr_key;
    // Handle backward compatibility: if task_category_mstr_keys array exists, convert to comma-separated string
    if (
      !taskCategoryMstrKey &&
      pageProps.location.state?.task_category_mstr_keys &&
      Array.isArray(pageProps.location.state.task_category_mstr_keys)
    ) {
      taskCategoryMstrKey = pageProps.location.state.task_category_mstr_keys.join(",");
    }
    let providerId = pageProps.location.state?.provider_id;
    let contractTypeMstrKey = pageProps.location.state?.contract_type_mstr_key;

    // Check sessionStorage as fallback for any missing values
    if (!taskCategoryMstrKey || !providerId) {
      const storedData = getPayRequestOverviewData();
      if (storedData) {
        // Use sessionStorage values only if location.state values are missing
        if (!taskCategoryMstrKey) {
          taskCategoryMstrKey = storedData.task_category_mstr_key;
        }
        if (!providerId) {
          providerId = storedData.provider_id;
        }
        // Keep session storage data for page refreshes - don't remove it here
      }
    }

    // Notification Access, so use fetched provider ID and null task category
    if (isNotificationAccess) {
      if (directAccessProviderId) {
        providerId = directAccessProviderId;
      }

      taskCategoryMstrKey = null;
    }

    // For notification access, wait until provider ID is fetched
    if (isNotificationAccess && !providerId) {
      return;
    }

    if (!startDate) {
      setDateErrors((prev) => ({ ...prev, startDate: "Start date is required" }));
      return;
    } else {
      setDateErrors((prev) => ({ ...prev, startDate: "" }));
    }

    if (!endDate) {
      setDateErrors((prev) => ({ ...prev, endDate: "End date is required" }));
      return;
    } else {
      setDateErrors((prev) => ({ ...prev, endDate: "" }));
    }

    setSelectedRowData(null);
    setIsRowSelected(false);

    // Use provided pagination params or current pageData state
    const paginationSkip = skip !== null ? skip : pageData.skip;
    const paginationTake = take !== null ? take : pageData.take;

    // A fixed multi-status filter (e.g. "8,3") passed in from a dashboard
    // widget takes precedence over the single-status tab selection, which
    // can't represent more than one status master key.
    const statusOverride = pageProps.location?.state?.in_status;
    const statusMstrKey =
      statusOverride !== undefined && statusOverride !== null
        ? statusOverride
        : getStatusKeyFromTab(selectedTab, statusList);

    // Use provided sort values or get from state
    const finalSortColumn = sortColumn !== null ? sortColumn : getInSortColumn();
    const finalSortNumber = sortNumber !== null ? sortNumber : getInSortNumber();
    const finalSortOrder = sortOrder !== null ? sortOrder : getInSortOrder();

    // Use provided column filters or create empty filter object
    const finalColumnFilters =
      columnFilters !== null
        ? columnFilters
        : {
            in_status_filter: null,
            in_location_name_filter: null,
            in_contract_type_name_filter: null,
            in_contract_name_filter: null,
            in_contract_type_mstr_key: null,
            in_description_filter: null,
            in_service_date_filter: null,
            in_post_date_filter: null,
            in_budget_filter: null,
            in_period_type_filter: null,
            in_shift_type_filter: null,
          };

    // Add contract type master key filter if provided
    if (contractTypeMstrKey) {
      finalColumnFilters.in_task_category_mstr_key = contractTypeMstrKey;
    }

    // Quantity breakdown key (e.g. "block" | "shift") chosen in the calendar's All Dates list
    const quantityTypeFromState = pageProps.location?.state?.quantity_type;
    if (quantityTypeFromState) {
      finalColumnFilters.in_thresold_unit_type = quantityTypeFromState;
    }

    // Add timesheet key filter if provided in location state
    const timesheetKeyFromState = pageProps.location?.state?.timesheet_key;
    if (timesheetKeyFromState) {
      const timesheetKeyFilter = ` AND t.timesheet_mstr_key = ${timesheetKeyFromState} `;
      finalColumnFilters.in_status_filter = timesheetKeyFilter;
    }

    // The position lists ride in the column filters, which getTimesheetsByProvider spreads into the body
    Object.assign(finalColumnFilters, getPositionScope(providerId));

    setAPICallingState(true);

    try {
      // Use selected date range
      const result = await getTimesheetsByProvider(
        cookiesData,
        dispatch,
        providerId,
        startDate,
        endDate,
        taskCategoryMstrKey,
        statusMstrKey,
        finalSortColumn,
        finalSortNumber,
        finalSortOrder,
        paginationSkip,
        paginationTake,
        finalColumnFilters
      );

      setPayRequestData(result.timesheets || []);
      setTotalRows(result.totalRows || 0);
    } catch (error) {
      console.error("Error fetching timesheets by provider:", error);
      setPayRequestData([]);
      setTotalRows(0);
      dispatch(
        setNewMessage({
          message: error?.message || "Failed to fetch timesheets",
          type: "Error",
          show: true,
        })
      );
    } finally {
      setAPICallingState(false);
      setInitialLoading(true);
      setRefreshLoading(false);
    }
  };

  // Export data function - fetches all data with filters applied
  const getExportData = async () => {
    // Clear previous export data to prevent stale data being used
    setExportData([]);

    // Guard: Don't export if a delete/refresh operation is in progress
    if (isBusy) {
      dispatch(
        setNewMessage({
          message: "Please wait for the current operation to complete",
          type: "Warning",
          show: true,
        })
      );
      setIsExportDataClick(false);
      return;
    }

    setRefreshLoading(true);

    // Small delay to ensure database transactions are fully committed
    await new Promise((resolve) => setTimeout(resolve, 2000));

    try {
      // Use gridColumns (with visibility state) for export, fallback to columns if gridColumns is empty
      const columnsToUse = gridColumns.length > 0 ? gridColumns : columns;

      // Extract column filters from filterField state
      const columnFilters = extractColumnFilters(filterField, columnsToUse);

      // Get current sort parameters
      const sortColumn = getInSortColumn();
      const sortNumber = getInSortNumber();
      const sortOrder = getInSortOrder();

      // Get status master key from selected tab
      const statusMstrKey = getStatusKeyFromTab(selectedTab, statusList);

      // Get provider and task category from location state or sessionStorage
      let taskCategoryMstrKey = pageProps.location.state?.task_category_mstr_key;
      if (
        !taskCategoryMstrKey &&
        pageProps.location.state?.task_category_mstr_keys &&
        Array.isArray(pageProps.location.state.task_category_mstr_keys)
      ) {
        taskCategoryMstrKey = pageProps.location.state.task_category_mstr_keys.join(",");
      }
      let providerId = pageProps.location.state?.provider_id;

      if (!taskCategoryMstrKey || !providerId) {
        const storedData = getPayRequestOverviewData();
        if (storedData) {
          if (!taskCategoryMstrKey) {
            taskCategoryMstrKey = storedData.task_category_mstr_key;
          }
          if (!providerId) {
            providerId = storedData.provider_id;
          }
        }
      }

      // Use selected date range
      // Build export request with all filters
      const exportColumnFilters = {
        ...columnFilters,
        // Set export flag to 1 for export
        in_export_flag: 1,
      };

      // Add contract type master key filter if provided
      const contractTypeMstrKeyExport = pageProps.location?.state?.contract_type_mstr_key;
      if (contractTypeMstrKeyExport) {
        exportColumnFilters.in_task_category_mstr_key = contractTypeMstrKeyExport;
      }

      const quantityTypeExport = pageProps.location?.state?.quantity_type;
      if (quantityTypeExport) {
        exportColumnFilters.in_thresold_unit_type = quantityTypeExport;
      }

      // Add timesheet key filter if provided in location state
      const timesheetKeyFromStateExport = pageProps.location?.state?.timesheet_key;
      if (timesheetKeyFromStateExport) {
        const timesheetKeyFilter = ` AND t.timesheet_mstr_key = ${timesheetKeyFromStateExport} `;
        exportColumnFilters.in_status_filter = timesheetKeyFilter;
      }

      Object.assign(exportColumnFilters, getPositionScope(providerId));

      // Fetch all data for export
      const result = await getTimesheetsByProvider(
        cookiesData,
        dispatch,
        directAccessProviderId || providerId,
        startDate,
        endDate,
        taskCategoryMstrKey,
        statusMstrKey,
        sortColumn,
        sortNumber,
        sortOrder,
        0, // pageRowOffset
        1000000, // pageRows - large number to get all records
        exportColumnFilters
      );

      if (result.timesheets && result.timesheets.length > 0) {
        // Format data for Excel export
        const formattedData = result.timesheets.map((item) => {
          const formattedItem = { ...item };

          // Format date fields - only process visible columns
          columnsToUse.forEach((col) => {
            if (col.type === "date" && col.field) {
              const fieldValue = formattedItem[col.field];
              if (fieldValue && fieldValue !== "0000-00-00 00:00:00" && fieldValue !== null) {
                try {
                  // Convert server date (America/New_York) to user's local timezone
                  const convertedDate = convertTimeZone({
                    date: fieldValue,
                    convertTimeZone: getUserTimezone(),
                    outputFormat: "MM/dd/yyyy",
                  });
                  if (convertedDate) {
                    formattedItem[col.field] = convertedDate;
                  } else {
                    formattedItem[col.field] = null;
                  }
                } catch (e) {
                  formattedItem[col.field] = null;
                }
              } else {
                formattedItem[col.field] = null;
              }
            }
            // Format number fields
            else if (col.type === "number" && col.field) {
              const fieldValue = formattedItem[col.field];
              if (fieldValue != null && fieldValue !== "") {
                let numericValue = parseFloat(fieldValue);
                if (!isNaN(numericValue)) {
                  // Format budget to 2 decimal places
                  if (col.field === "budget") {
                    formattedItem[col.field] = numericValue.toFixed(2);
                  }
                  // For ID fields (like timesheet_mstr_key), keep as integer (no decimals)
                  else if (col.field.includes("_mstr_key")) {
                    formattedItem[col.field] = Math.floor(numericValue).toString();
                  }
                  // Other numbers to 2 decimal places
                  else {
                    formattedItem[col.field] = numericValue.toFixed(2);
                  }
                } else {
                  formattedItem[col.field] = null;
                }
              } else {
                formattedItem[col.field] = null;
              }
            }
          });

          // Format budget specifically (ensure 2 decimal places)
          if (formattedItem.budget !== null && formattedItem.budget !== undefined && formattedItem.budget !== "") {
            formattedItem.budget = Number(formattedItem.budget).toFixed(2);
          }

          // Format rate specifically (ensure 2 decimal places)
          if (formattedItem.rate !== null && formattedItem.rate !== undefined && formattedItem.rate !== "") {
            formattedItem.rate = Number(formattedItem.rate).toFixed(2);
          }

          formattedItem.total_quantity = formatTimesheetQuantity(formattedItem);

          // Add timesheet_name field for export (DataTable maps timesheet_mstr_key to timesheet_name)
          if (formattedItem.timesheet_mstr_key !== null && formattedItem.timesheet_mstr_key !== undefined) {
            formattedItem.timesheet_name = formattedItem.timesheet_mstr_key.toString();
          }

          if (formattedItem.status) {
            formattedItem.status = formattedItem.status.display_name;
          }

          return formattedItem;
        });

        setExportData(formattedData);
      } else {
        setExportData([]);
        dispatch(
          setNewMessage({
            message: "No data available to export",
            type: "Warning",
            show: true,
          })
        );
      }
    } catch (error) {
      console.error("Error exporting data:", error);
      setExportData([]);
      dispatch(
        setNewMessage({
          message: error?.message || "Failed to export data",
          type: "Error",
          show: true,
        })
      );
    } finally {
      setIsExportDataClick(false);
      setRefreshLoading(false);
    }
  };

  // Handle menu item clicks (for export and delete)
  const handleMenuItemClick = (module) => {
    switch (module) {
      case "export":
        // Prevent export if no records
        if (payRequestData.length === 0) {
          dispatch(
            setNewMessage({
              message: "No data available to export",
              type: "Warning",
              show: true,
            })
          );
          return;
        }
        setIsExportDataClick(true);
        break;
      default:
        break;
    }
  };

  const BackBtn = () => {
    return (
      <Tooltip anchorElement="target" position="bottom" openDelay={100}>
        <Button
          onClick={() => history.push("/pay_request_overview")}
          title="Back to Provider Dashboard"
          type="button"
          className="cancelbtn backbtn"
          id="grid-back-btn"
        >
          <span className="k-button-text">
            <i className="fa fa-arrow-left"></i>
          </span>
        </Button>
      </Tooltip>
    );
  };

  const renderPositionFilter = () => (
    <PositionFilter rows={profileRows} selectedKeys={positionFilterKeys} onChange={handlePositionFilterChange} />
  );

  const headerConfig = {
    title: "Pay Request Overview",
    id: "pay_request_overview_overview",
    header_actions_fx: showPositionFilter ? renderPositionFilter : null,
    action_menu: {
      export: {
        fx: handleMenuItemClick,
        disabled: payRequestData.length === 0 || isBusy,
      },
    },
    manage_col: {
      columnsData: columnsData,
      setColumnsData: setColumnsData,
      setColumnCheck: setColumnCheck,
    },
    backBtn: { btn_fx: BackBtn },
  };

  React.useEffect(() => {
    // When switching tabs, clear all column filters and sorting so each tab
    // starts with a clean grid state and default sorting.
    setFilterField({ logic: "and", filters: [] });
    setSortField([]);
    setFilterData({
      isSort: false,
      isFilter: false,
      data: "",
      in_sort_column: "t.timesheet_mstr_key",
      in_sort_number: DEFAULT_SORT.number,
      sort_order: DEFAULT_SORT.order,
    });

    // Reset to first page using current page size
    setPageData({ skip: 0, take: pageSize.recordTake });

    // Ask DataTable/Kendo Grid to reset its internal sort/filter state & UI
    setResetGrid(true);

    // Fetch data for the newly selected tab with default sort and no column filters
    fetchTimesheetsByProvider(
      0,
      pageSize.recordTake,
      "t.timesheet_mstr_key",
      DEFAULT_SORT.number,
      DEFAULT_SORT.order,
      null
    );
  }, [selectedTab]);

  // Reset the resetGrid flag after it's been processed by DataTable
  React.useEffect(() => {
    if (resetGrid) {
      const timer = setTimeout(() => {
        setResetGrid(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [resetGrid]);

  // Search useEffect - commented out as search bar is removed
  // React.useEffect(() => {
  //     const debounceTimer = setTimeout(() => {
  //         if (searchValue !== undefined) {
  //             setPageData({ skip: 0, take: pageData.take });
  //             fetchTimesheetsByProvider(0, pageData.take);
  //         }
  //     }, 500); // 500ms debounce
  //
  //     return () => clearTimeout(debounceTimer);
  // }, [searchValue]);

  React.useEffect(() => {
    // Skip initial fetch if this is notification access
    const queryParams = getQueryParams();
    const hasLocationState = pageProps.location?.state?.provider_id;
    const hasSessionData = getPayRequestOverviewData()?.provider_id;
    const isNotificationLink = queryParams.timesheetId || queryParams.status;

    if (isNotificationLink && !hasLocationState && !hasSessionData) {
      // Notification access - wait for provider ID to be fetched
      return;
    }

    fetchTimesheetsByProvider();
  }, []);

  // Detect notification access and fetch provider ID
  React.useEffect(() => {
    const queryParams = getQueryParams();
    const hasLocationState = pageProps.location?.state?.provider_id;
    const hasSessionData = getPayRequestOverviewData()?.provider_id;

    // Store timesheet ID from URL for auto-opening dialog
    if (queryParams.timesheetId) {
      setAutoOpenTimesheetId(queryParams.timesheetId);
    }

    // If notification access, fetch provider ID
    const isNotificationAccess = !hasLocationState && !hasSessionData;
    if (isNotificationAccess) {
      setIsDirectAccess(true);
      setIsLoadingProvider(true);
    }

    // The profiles feed the Position filter on every visit, and the provider on notification access
    getProviderProfiles(cookiesData, dispatch)
      .then((rows) => {
        // Row 0 stays the provider; the rows give the grid All Positions. Set the rows
        // first: the fetch effect runs on the provider id and reads them.
        setProfileRows(rows);
        if (!isNotificationAccess) return;
        const providerId = rows[0]?.provider_mstr_key;
        if (providerId) {
          setDirectAccessProviderId(providerId);
        } else {
          dispatch(
            setNewMessage({
              message: "Unable to determine provider.",
              type: "Error",
              show: true,
            })
          );
        }
      })
      .finally(() => {
        if (isNotificationAccess) setIsLoadingProvider(false);
      });
  }, []);

  // A Position filter change reloads the grid from the first page, keeping the column filters and sorting
  React.useEffect(() => {
    if (pickedPositionKeys === null) return;
    onRefreshClick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedPositionKeys]);

  // Fetch data after provider ID is fetched for notification access
  React.useEffect(() => {
    if (isDirectAccess && directAccessProviderId) {
      fetchTimesheetsByProvider();
    }
  }, [directAccessProviderId]);

  // Auto-open ViewPayRequestDialog when accessing via notification
  React.useEffect(() => {
    if (autoOpenTimesheetId && initialLoading && !isLoadingProvider) {
      const rowDataForDialog = {
        timesheet_mstr_key: parseInt(autoOpenTimesheetId, 10),
        provider_id:
          directAccessProviderId || pageProps.location?.state?.provider_id || getPayRequestOverviewData()?.provider_id,
      };

      setSelectedRowData(rowDataForDialog);
      setViewDialogVisible(true);

      // Clear the auto-open flag so it doesn't reopen on subsequent notifications
      setAutoOpenTimesheetId(null);
    }
  }, [autoOpenTimesheetId, initialLoading, isLoadingProvider]);

  // Update gridColumns when columnsData changes via manage columns UI
  React.useEffect(() => {
    if (isCheckColumn && columnsData.length > 0) {
      setGridColumns(columnsData);
      setColumnCheck(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckColumn]);

  // Define columns for DataTable - memoized to prevent recreation on every render
  const columns = useMemo(
    () => [
      {
        field: "timesheet_mstr_key",
        title: "TIMESHEET ID",
        width: "120px",
        show: true,
        type: "number",
        filterable: 1,
      },
      {
        field: "status",
        title: "STATUS",
        width: "200px",
        show: true,
        type: "string",
        filterable: 1,
        cell: StatusCell,
      },
      {
        field: "location_name",
        title: "LOCATION",
        width: "250px",
        show: true,
        type: "string",
        filterable: 1,
        cell: (props) => {
          const value = props.dataItem.location_name || "";
          return (
            <td className="k-table-td">
              <span title={value}>{value}</span>
            </td>
          );
        },
      },
      {
        field: "contracT_type_name",
        title: "PAY REQUEST TYPE",
        width: "150px",
        show: true,
        type: "string",
        filterable: 1,
        cell: (props) => {
          const value = props.dataItem.contracT_type_name || "";
          return (
            <td className="k-table-td">
              <span title={value}>{value}</span>
            </td>
          );
        },
      },
      {
        field: "contract_name",
        title: "PAY REQUEST NAME",
        width: "220px",
        show: true,
        type: "string",
        filterable: 1,
        cell: (props) => {
          const value = props.dataItem.contract_name || "";
          return (
            <td className="k-table-td">
              <span title={value}>{value}</span>
            </td>
          );
        },
      },
      {
        field: "shift_type_name",
        title: "SHIFT TYPE",
        width: "150px",
        show: true,
        type: "string",
        filterable: 1,
        cell: (props) => {
          const value = props.dataItem.shift_type_name || "";
          return (
            <td className="k-table-td">
              <span title={value}>{value}</span>
            </td>
          );
        },
      },
      {
        field: "ts_description",
        title: "DESCRIPTION",
        width: "200px",
        show: true,
        type: "string",
        filterable: 1,
        cell: (props) => {
          const value = props.dataItem.ts_description || "";
          return (
            <td className="k-table-td">
              <span title={value}>{value}</span>
            </td>
          );
        },
      },
      {
        field: "ts_date",
        title: "SERVICE DATE",
        width: "150px",
        show: true,
        type: "date",
        format: "{0:MM/dd/yyyy}",
        filterable: 1,
        cell: (props) => {
          const dateValue = props.dataItem.ts_date;
          const formattedDate = dateValue
            ? convertTimeZone({
                date: dateValue,
                convertTimeZone: getUserTimezone(),
                outputFormat: "MM/dd/yyyy",
              }) || ""
            : "";
          return (
            <td className="k-table-td">
              <span title={formattedDate}>{formattedDate}</span>
            </td>
          );
        },
      },
      {
        field: "post_date",
        title: "POST DATE",
        width: "150px",
        show: true,
        type: "date",
        format: "{0:MM/dd/yyyy}",
        filterable: 1,
        cell: (props) => {
          const dateValue = props.dataItem.post_date;
          const formattedDate = dateValue
            ? convertTimeZone({
                date: dateValue,
                convertTimeZone: getUserTimezone(),
                outputFormat: "MM/dd/yyyy",
              }) || ""
            : "";
          return (
            <td className="k-table-td">
              <span title={formattedDate}>{formattedDate}</span>
            </td>
          );
        },
      },
      {
        field: "total_quantity",
        title: "TOTAL QUANTITY",
        width: "150px",
        show: true,
        type: "string",
        filterable: 0,
        cell: (props) => {
          const formatted = formatTimesheetQuantity(props.dataItem);
          return (
            <td className="k-table-td">
              <span title={formatted}>{formatted}</span>
            </td>
          );
        },
        sortable: false,
      },
      {
        field: "budget",
        title: "AMOUNT",
        width: "120px",
        show: true,
        type: "number",
        filterable: 0,
        cell: (props) => {
          const budget = props.dataItem.budget;
          const formattedBudget = budget ? Number(budget).toFixed(2) : "0.00";
          return <td className="k-table-td">{formattedBudget}</td>;
        },
        sortable: false,
      },
      {
        field: "period_type",
        title: "PERIOD TYPE",
        width: "150px",
        show: true,
        type: "string",
        filterable: 1,
        cell: (props) => {
          const periodType = props.dataItem.period_type || "";
          const formatted = periodType.charAt(0).toUpperCase() + periodType.slice(1);
          return (
            <td className="k-table-td">
              <span title={formatted}>{formatted}</span>
            </td>
          );
        },
      },
    ],
    []
  );

  // Initialize columnsData from columns array only once on mount
  React.useEffect(() => {
    if (columns.length > 0 && columnsData.length === 0) {
      setColumnsData(columns);
      setGridColumns(columns);
    }
  }, []);

  // Trailing Action column. Edit and Delete are offered on the same condition â€”
  // only a New or Rejected request is still the provider's to change.
  const ActionCell = (props) => {
    const { ariaColumnIndex, columnIndex, dataItem, render } = props;
    const isActionable = selectedRowIsNewStatus(dataItem);

    const iconClass = (icon) =>
      hasWriteAccess ? `k-icon k-font-icon ${icon} pointer-class` : `k-icon k-font-icon ${icon} disable-class`;

    // `disable-class` only changes the cursor, so dim the glyph as well â€”
    // otherwise a read-only user cannot tell the actions are unavailable.
    const iconStyle = hasWriteAccess ? undefined : { opacity: 0.4 };

    // The grid selects a row on click and opens the detail view on
    // double-click, so an action click must not reach the row underneath it.
    const runAction = (event, action) => {
      event.stopPropagation();
      if (!hasWriteAccess) return;
      action(dataItem);
    };

    const defaultRendering = (
      <td
        aria-colindex={ariaColumnIndex}
        data-grid-col-index={columnIndex}
        className="k-table-td"
        style={{ textAlign: "center", verticalAlign: "middle" }}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        {isActionable && (
          <>
            <span
              title="Edit Pay Request"
              style={{ marginRight: 12, ...iconStyle }}
              className={iconClass("k-i-edit")}
              onClick={(e) => runAction(e, handleEditRow)}
            />
            <span
              title="Delete Pay Request"
              style={iconStyle}
              className={iconClass("k-i-trash")}
              onClick={(e) => runAction(e, handleDeleteRow)}
            />
          </>
        )}
      </td>
    );

    return render?.call(undefined, defaultRendering, props) ?? defaultRendering;
  };

  // Generate customColumn from gridColumns array (which reflects manage columns changes)
  const customColumn = gridColumns.map((column, idx) => {
    if (!column.show || !column.title) return null;

    const columnProps = {
      ...columnMenuProps(column, "pay-request-overview"),
      key: idx,
      field: column.field,
      title: column.title,
      width: column.width,
      sortable: column.sortable !== false,
      filterable: column.filterable === 1,
    };

    // Add cell renderer for columns that have custom cells
    if (column.cell) {
      columnProps.cell = column.cell;
    }

    return <Column {...columnProps} />;
  });

  // Appended here rather than added to `columns` on purpose. `columns` is a
  // useMemo with an empty dep array that is copied once into `gridColumns`
  // state, so a `cell` declared there would keep its mount-time closure
  // forever. Living outside that array also keeps the column out of Manage
  // Columns, drag-reorder and the Excel export, and pins it last.
  const gridColumnsWithActions = [
    ...customColumn,
    <Column
      key="action"
      title="ACTION"
      width="110px"
      sortable={false}
      filterable={false}
      reorderable={false}
      cell={ActionCell}
    />,
  ];

  // Calculate grid height based on error message
  const calculateGridHeight = () => {
    const isDateError = dateErrors.startDate || dateErrors.endDate;

    if (isDateError) {
      return "55vh";
    } else {
      return "58vh";
    }
  };

  const gridHeight = calculateGridHeight();

  return (
    <div className="Main payRequestOverviewPage">
      <div className="content">
        <div className="content-head">
          <HeaderPage pageProps={pageProps} headerConfig={headerConfig} />
        </div>
        <div className="content-body">
          {isLoadingProvider && (
            <div
              className="loading-overlay"
              style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 100 }}
            >
              <LoadingPanel gridId="pay-request-overview-provider-loading" />
            </div>
          )}
          <div className="pay-request-overview-content">
            <div className="pay-request-overview-tabs">
              <div className="custom-tabs-container">
                <div className="custom-tabs">
                  <div className={`custom-tab ${selectedTab === 0 ? "active" : ""}`} onClick={() => handleTabSelect(0)}>
                    ALL
                  </div>
                  {statusList.map((status, index) => (
                    <div
                      key={status.timesheet_status_mstr_key}
                      className={`custom-tab ${selectedTab === index + 1 ? "active" : ""}`}
                      onClick={() => handleTabSelect(index + 1)}
                    >
                      {status.display_name}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="pay-request-overview-controls">
              <div className="filter-row">
                <div className="filter-item">
                  <label className="filter-label">Start Date</label>
                  <DatePicker
                    value={startDate}
                    onChange={handleStartDateChange}
                    placeholder="Start Date"
                    className="filter-datepicker"
                  />
                  {dateErrors.startDate && <div className="date-validation-error">{dateErrors.startDate}</div>}
                </div>
                <div className="filter-item">
                  <label className="filter-label">End Date</label>
                  <DatePicker
                    value={endDate}
                    onChange={handleEndDateChange}
                    placeholder="End Date"
                    className="filter-datepicker"
                  />
                  {dateErrors.endDate && <div className="date-validation-error">{dateErrors.endDate}</div>}
                </div>
                <div className="filter-item search-button-container">
                  <label className="filter-label">&nbsp;</label>
                  <Tooltip anchorElement="target" position="top" openDelay={100}>
                    <button
                      className="search-button"
                      onClick={handleFetchData}
                      disabled={isBusy || !!dateErrors.startDate || !!dateErrors.endDate}
                      title="Filter Data"
                    >
                      <span className="k-icon k-font-icon k-i-search" />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>

            <div>
              {isRefreshLoading && loadingPanel}
              <DataTable
                isAPICalling={isAPICalling}
                initialLoading={initialLoading}
                data={payRequestData}
                columns={gridColumns.length > 0 ? gridColumns : columns}
                customColumn={gridColumnsWithActions}
                gridHeight={gridHeight}
                isPagination={true}
                isSortable={true}
                isResizable={true}
                scrollable="scrollable"
                id="pay-request-overview-grid"
                dataItemKey="employeeId"
                pageData={pageData}
                dataCount={totalRows}
                handlePageChange={handlePageChange}
                pageSize={pageSize}
                setPageSize={setPageSize}
                onRowClick={handleRowClick}
                rowRender={customRowRender}
                handleColumnMenu={handleColumnMenu}
                onClearFilter={onClearFilter}
                initialFilter={filterField}
                initialSort={sortField}
                isExportDataClick={isExportDataClick}
                setExportData={setIsExportDataClick}
                getExportData={getExportData}
                exportData={exportData}
                fileName={fileName}
                refreshClick={onRefreshClick}
                resetSort={resetGrid}
                columnReorderData={handleColumnReorder}
                onRowDoubleClick={handleRowDoubleClick}
              />
            </div>
          </div>
        </div>
      </div>

      {editTask && (
        // Keyed so switching rows remounts the dialog: it seeds its month from
        // `task` in a lazy useState initializer, which will not re-run on a
        // prop change alone.
        <AddPayRequestDialog
          key={editTask.timesheet_mstr_key}
          visible
          task={editTask}
          providerId={editTask.provider_id}
          orgPositionKey={editTask.org_position_key ?? 0}
          onClose={() => setEditTask(null)}
          onSaved={() => {
            setEditTask(null);
            refetchGrid();
          }}
        />
      )}

      {viewDialogVisible && (
        <ViewPayRequestDialog
          visible={viewDialogVisible}
          onClose={() => setViewDialogVisible(false)}
          providerId={selectedRowData?.provider_id}
          rowData={selectedRowData}
        />
      )}

      <DialogComponent
        title={confirmationPopup.title}
        onCancelConfirm={onCancelConfirm}
        width={"450"}
        height={"auto"}
        className={"Dialog-Delete"}
        desc={confirmationPopup.desc}
        visible={confirmationPopup.isVisible}
        actionBtn={confirmationPopup.actionBtn}
        titleClassName={"delete-confirm"}
      />
    </div>
  );
};

export default PayRequestOverview;
